import { Component, OnInit, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgStyle } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MatRippleModule } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { EmployeeService, PermissionsService } from 'src/app/services';
import { MyCompanyProductRoles } from 'src/app/models/my_company_product_roles.model';

type PartnerMenuSection = 'marketing' | 'support' | 'rrhh' | 'comercial' | 'direction' | 'wiki';

@Component({
  selector: 'app-lateral-menu-partner',
  templateUrl: './lateral-menu-partner.component.html',
  styleUrl: './lateral-menu-partner.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterLinkActive, RouterLink, MatIcon, MatRippleModule, NgStyle],
})
export class LateralMenuPartnerComponent implements OnInit {
  readonly permissions = inject(PermissionsService);
  readonly employee = inject(EmployeeService);
  private readonly router = inject(Router);

  private readonly my_company_product_roles = toSignal(this.permissions.myCompanyProductRolesObservable, {
    initialValue: null as MyCompanyProductRoles | null,
  });

  readonly company_products_list = computed(() => Object.values(this.my_company_product_roles() ?? {}));

  private readonly expanded: Record<PartnerMenuSection, boolean> = {
    marketing: true,
    support: true,
    rrhh: true,
    comercial: true,
    direction: true,
    wiki: true,
  };

  ngOnInit(): void {}

  openPendingSlackChats(event: Event): void {
    event.preventDefault();
    void this.router.navigate(['/partners', 'my-profile', 'chats']);
  }

  canSeeProjects(): boolean {
    if (
      this.permissions.isDepartment('development') ||
      this.permissions.isDepartment('designers') ||
      this.permissions.isDepartment('direction')
    ) {
      return true;
    }

    return this.company_products_list().length > 0;
  }

  isExpanded(section: PartnerMenuSection): boolean {
    return this.expanded[section];
  }

  isSectionActive(section: PartnerMenuSection): boolean {
    const prefixes: Record<PartnerMenuSection, string> = {
      marketing: '/partners/marketing',
      support: '/partners/support',
      rrhh: '/partners/rrhh',
      comercial: '/partners/comercial',
      direction: '/partners/direction',
      wiki: '/partners/wiki',
    };
    return this.router.url.includes(prefixes[section]);
  }

  isCompanyProductActive(company_product_id: number): boolean {
    const match = this.router.url.match(/\/partners\/company-products\/(\d+)/);
    return match?.[1] === String(company_product_id);
  }

  goToCompanyProduct(company_product_id: number): void {
    void this.router.navigate(['/partners', 'company-products', company_product_id, 'okr-and-metrics']);
  }

  toggle(section: PartnerMenuSection): void {
    this.expanded[section] = !this.expanded[section];
  }

  onSectionKeydown(event: KeyboardEvent, section: PartnerMenuSection): void {
    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    this.toggle(section);
  }
}
